import { useState } from "react";
import { Search, Tag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { CONTROL_ACTIVE_CLASS, CONTROL_FIELD_CLASS } from "@/components/ui/control-scale";
import { cn } from "@/lib/utils";
import { IssueFilterControl } from "./IssueFilterControl";
import {
  buildIssueFilterChips,
  EMPTY_ISSUE_FILTER,
  type IssueFilterDimension,
  type IssueFilterValue,
} from "./issueFilters";
import { IssueFilterChips } from "./IssueFilterChips";
import { IssueSortControl } from "./IssueSortControl";
import { DisciplineBadges } from "@/domains/pm/components/shared/DisciplineBadges";
import type { Discipline, IssueStatus, UserProfile } from "@/domains/pm/types";
import type { IssueGroupBy } from "@/domains/pm/utils/groupAndSort";
import { pageInfoTarget } from "@/components/page-info/pageInfoTarget";

interface IssuesToolbarProps {
  users: UserProfile[];
  disciplines: Discipline[];
  filter: {
    value: IssueFilterValue;
    onChange: (next: IssueFilterValue) => void;
    /** Dimensions this page does not offer — see `IssueFilterControl`. */
    hiddenDimensions?: IssueFilterDimension[];
    /** Scope-wide per-status totals for the status dimension's rows. */
    statusCounts?: Record<IssueStatus, number>;
    /** The project in scope, or null for "All projects". */
    buildingProjectId?: number | null;
    /** Opens the "New status" dialog; omitted when the page cannot offer it. */
    onCreateStatus?: () => void;
    /** Scope-wide overdue total for the overdue dimension's badge. */
    overdueCount?: number;
  };
  sort: {
    value: IssueGroupBy;
    onChange: (next: IssueGroupBy) => void;
    /** Shown for List and Gantt; the kanban's columns already ARE its
     *  grouping, so a second grouping control there would contradict it. */
    show: boolean;
  };
  search: { value: string; onChange: (next: string) => void };
  /**
   * The viewer's Responsibilities in the project in scope (Contacts). When
   * non-empty the toolbar offers a one-click "My disciplines" filter.
   */
  myDisciplineIds?: number[];
  /** Layout the host row gives the toolbar — it owns no outer margin itself. */
  className?: string;
}

/** The toolbar's search box — matches the issue text, on the shared control scale. */
function IssueSearchInput({
  value,
  onChange,
}: {
  value: string;
  onChange: (next: string) => void;
}) {
  const { t } = useLanguage();
  return (
    // WHA-3407: the icon is positioned inside the input so there is a single
    // bordered box (a wrapper around the input double-rendered its focus ring
    // as a stray white box).
    <div className="relative w-56">
      <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={t.pm.issues.search}
        className={cn(CONTROL_FIELD_CLASS, "pl-8")}
      />
    </div>
  );
}

function sameIds(a: readonly number[], b: readonly number[]): boolean {
  return a.length === b.length && a.every((id) => b.includes(id));
}

/**
 * "My disciplines": sets the discipline filter to the viewer's own
 * Responsibilities, and clears it when pressed again. It only fills the
 * ordinary discipline filter, so the chip row shows what is applied and the
 * filter can be edited from there like any other.
 */
function MyDisciplinesToggle({
  ids,
  disciplines,
  filter,
}: {
  ids: number[];
  disciplines: Discipline[];
  filter: IssuesToolbarProps["filter"];
}) {
  const { t } = useLanguage();
  const active = sameIds(filter.value.disciplineIds, ids);
  const mine = disciplines.filter((d) => ids.includes(d.id));
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          {...pageInfoTarget("issues.myDisciplines")}
          variant="outline"
          size="control"
          aria-pressed={active}
          className={cn("gap-1.5", active && CONTROL_ACTIVE_CLASS)}
          onClick={() =>
            filter.onChange({ ...filter.value, disciplineIds: active ? [] : [...ids] })
          }
        >
          <Tag className="opacity-60" />
          {t.pm.issues.myDisciplines}
        </Button>
      </TooltipTrigger>
      <TooltipContent className="max-w-72">
        <p className="mb-1.5">{t.pm.issues.myDisciplinesTooltip}</p>
        {mine.length > 0 && (
          <div className="flex flex-wrap gap-1">
            <DisciplineBadges disciplines={mine} size="sm" variant="outline" />
          </div>
        )}
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * The issues toolbar, shared by the Issues and My Issues pages.
 *
 * One line, with the whole button cluster pinned to the right in a fixed
 * Sort · Filter · Search order. The buttons stay quiet and compact until they
 * have something to say — a count badge, a chosen sort — and the width they
 * gain then comes out of the chip row beside them, so the search box keeps its
 * place at the right edge. Active filter chips render on the left of the
 * cluster and grow leftward into the empty header space, collapsing their
 * oldest members into a "+N more" pill rather than wrapping or pushing the
 * buttons.
 */
export function IssuesToolbar({
  users,
  disciplines,
  filter,
  sort,
  search,
  myDisciplineIds,
  className,
}: IssuesToolbarProps) {
  const { t, language } = useLanguage();
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterStep, setFilterStep] = useState<IssueFilterDimension | null>(null);

  const chips = buildIssueFilterChips(
    filter.value,
    users,
    disciplines,
    t.pm.issues.filterBy,
    language,
    filter.onChange,
    filter.hiddenDimensions,
    t.pm.issues.status
  );

  const openFilterAt = (dimension: IssueFilterDimension) => {
    setFilterStep(dimension);
    setFilterOpen(true);
  };

  // flex-wrap is the narrow-viewport escape hatch: the chips shrink to
  // nothing first, and only when the cluster itself no longer fits does it
  // wrap onto a second line (as the pre-toolbar rows did).
  //
  // No outer margin of its own: both pages seat the toolbar on their tab row,
  // which owns the spacing. A host that wants it on its own line passes one.
  return (
    <div className={cn("flex flex-wrap items-center gap-4", className)}>
      <IssueFilterChips
        chips={chips}
        onOpenDimension={openFilterAt}
        onClearAll={() => filter.onChange(EMPTY_ISSUE_FILTER)}
        users={users}
        buildingProjectId={filter.buildingProjectId}
      />

      <div className="flex shrink-0 items-center gap-2">
        {sort.show && <IssueSortControl value={sort.value} onChange={sort.onChange} />}

        {myDisciplineIds && myDisciplineIds.length > 0 && (
          <MyDisciplinesToggle ids={myDisciplineIds} disciplines={disciplines} filter={filter} />
        )}

        {/* WHA-3423: cascading filter box. */}
        <IssueFilterControl
          users={users}
          disciplines={disciplines}
          value={filter.value}
          onChange={filter.onChange}
          hiddenDimensions={filter.hiddenDimensions}
          statusCounts={filter.statusCounts}
          overdueCount={filter.overdueCount}
          buildingProjectId={filter.buildingProjectId}
          onCreateStatus={filter.onCreateStatus}
          activeCount={chips.length}
          open={filterOpen}
          onOpenChange={setFilterOpen}
          step={filterStep}
          onStepChange={setFilterStep}
        />

        <IssueSearchInput value={search.value} onChange={search.onChange} />
      </div>
    </div>
  );
}
